import { csvStringFromReactCsvPayload } from './cashflowCsvExportUtils';

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatMonthYear = ({ year, month }) =>
  `${MONTH_LABELS[month - 1]}-${String(year).slice(-2)}`;

const dateKey = ({ year, month }) =>
  `${year}-${String(month).padStart(2, '0')}`;

/**
 * @param {Record<string, unknown> | null | undefined} modelResults Full calc result (not just items).
 * @returns {{ headers: { label: string, key: string }[], data: Record<string, unknown>[] } | null}
 */
export function buildRepaymentsPerCohortCsvPayload(modelResults) {
  const breakdown =
    modelResults?.fund_repayment_breakdown ??
    modelResults?.fundRepaymentBreakdown;
  if (!breakdown || breakdown.length === 0) {
    return null;
  }

  const periodKeySet = new Set();
  breakdown.forEach((fund) => {
    (fund.repayments || []).forEach((r) => {
      if (r.date) {
        periodKeySet.add(dateKey(r.date));
      }
    });
  });
  const periodKeys = Array.from(periodKeySet).sort();
  const periodDates = periodKeys.map((k) => {
    const [year, month] = k.split('-').map(Number);
    return { year, month };
  });

  const headers = [
    { label: 'cohort', key: 'cohort' },
    ...periodDates.map((d, idx) => ({
      label: formatMonthYear(d),
      key: `period_${idx}`,
    })),
  ];

  const rows = breakdown.map((fund) => {
    const fundDate = fund.fund_date ? formatMonthYear(fund.fund_date) : '';
    const row = { cohort: fundDate };

    const repaymentByPeriod = {};
    (fund.repayments || []).forEach((r) => {
      if (r.date) {
        repaymentByPeriod[dateKey(r.date)] = r.amount;
      }
    });

    periodDates.forEach((_, idx) => {
      const amount = repaymentByPeriod[periodKeys[idx]];
      row[`period_${idx}`] =
        amount != null && amount !== 0 ? amount.toFixed(2) : '';
    });

    return row;
  });

  const totalRow = { cohort: 'total repayment per mob' };
  periodDates.forEach((_, idx) => {
    const sum = breakdown.reduce((acc, fund) => {
      const repaymentByPeriod = {};
      (fund.repayments || []).forEach((r) => {
        if (r.date) {
          repaymentByPeriod[dateKey(r.date)] = r.amount;
        }
      });
      return acc + (repaymentByPeriod[periodKeys[idx]] || 0);
    }, 0);
    totalRow[`period_${idx}`] = sum !== 0 ? sum.toFixed(2) : '';
  });

  return { headers, data: [...rows, {}, totalRow] };
}

/** @param {Record<string, unknown> | null | undefined} modelResults */
export function buildRepaymentsPerCohortCsvString(modelResults) {
  const payload = buildRepaymentsPerCohortCsvPayload(modelResults);
  if (!payload) {
    return '';
  }
  return csvStringFromReactCsvPayload(payload);
}
